import React, { useMemo } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Button,
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@pagespeedify/ui';
import { CopyIcon } from 'lucide-react';
import { toast } from 'sonner';
import { LcpElementDetails } from './lcpAndAuditRefExtractors';

// --- TypeScript Interfaces ---
interface DetailsTableItem {
    [key: string]: any;
    _groupHeader?: string; // Optional: for group headers
    _tags?: string[];      // Optional: for tags/labels
}

interface DetailsTableHeading {
    key: string;
    label: string;
    itemType?: string; // e.g., 'url', 'thumbnail', 'text'
    valueType?: string; // e.g., 'bytes', 'ms'
}

interface DetailsTable {
    headings: DetailsTableHeading[];
    items: DetailsTableItem[];
    type: 'table' | 'opportunity';
    savings?: number; // Est. savings in bytes/ms/other
    savingsDisplay?: string; // e.g. 'Est savings of 313 KiB'
}

interface DetailsList {
    type: 'list';
    items: any[];
}

interface DetailsScreenshot {
    type: 'screenshot';
    data: string;
}

interface DetailsFilmstrip {
    type: 'filmstrip';
    items: { data: string }[];
}

interface PluginTip {
    type: 'pluginTip';
    message: string;
    icon?: string;
}

type DiagnosticDetailsData = DetailsTable | DetailsList | DetailsScreenshot | DetailsFilmstrip | PluginTip;

interface Diagnostic {
    id?: string; // Add id for mapping
    title: string;
    description: string;
    score?: number | null;
    displayValue?: string;
    numericValue?: number;
    details?: DiagnosticDetailsData;
    estSavings?: string; // e.g. 'Est savings of 313 KiB'
    severity?: 'error' | 'warning' | 'info';
    pluginTip?: PluginTip;
    extractedItems?: any[]; // Add extractedItems for dynamic table
    lcpDetails?: LcpElementDetails | null; // For detailed LCP data
}

interface DiagnosticsProps {
    reportData: {
        diagnostics?: Diagnostic[];
    };
    // openItems and setOpenItems are optional for compatibility
    openItems?: string[];
    setOpenItems?: (items: string[]) => void;
}

// --- Helper Functions ---
const formatBytes = (bytes: number, decimals = 2): string => {
    if (typeof bytes !== 'number' || isNaN(bytes)) return '—';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const formatTime = (ms: number, decimals = 0): string => {
    if (typeof ms !== 'number' || isNaN(ms)) return '—';
    if (ms < 1000) return `${ms.toFixed(decimals)} ms`;
    return `${(ms / 1000).toFixed(decimals)} s`;
};

const renderTag = (tag: string) => (
    <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded mr-1 mb-1 align-middle font-semibold border border-blue-200">
        {tag}
    </span>
);

const renderValue = (value: any, heading?: DetailsTableHeading): React.ReactNode => {
    if (typeof value === 'string') {
        if (/\.(png|jpe?g|webp|gif|svg)$/i.test(value) || value.startsWith('data:image/')) {
            return <img src={value} alt="diagnostic-thumbnail" className="max-w-xs max-h-40 rounded shadow border mb-2 block" />;
        }
        if (/^https?:\/\//.test(value)) {
            return <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">{value}</a>;
        }
        if (/^[\s\S]*[{};=<>\[\]]+[\s\S]*$/.test(value) && value.length < 1000) {
            return <pre className="bg-gray-900 text-white p-3 rounded-md text-xs font-mono overflow-x-auto mb-2"><code>{value}</code></pre>;
        }
        return value;
    }
    if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
            // Only render as a list if the array is not a list of objects with many keys
            if (value.length > 0 && typeof value[0] === 'object' && Object.keys(value[0]).length > 2) {
                return <span>—</span>; // Do not render raw object arrays
            }
            return (
                <ul className="list-disc list-inside text-xs bg-muted p-2 rounded mb-2">
                    {value.map((v, i) => <li key={i}>{renderValue(v.richText || v)}</li>)}
                </ul>
            );
        }
        if (value.code && typeof value.code === 'string') {
            return <pre className="bg-gray-900 text-white p-3 rounded-md text-xs font-mono overflow-x-auto mb-2"><code>{value.code}</code></pre>;
        }
        if (value.richText) {
            return renderValue(value.richText);
        }
        // Do not render raw JSON for unknown objects
        return <span>—</span>;
    }
    if (typeof value === 'number') {
        if (heading?.valueType === 'bytes' || heading?.key === 'transferSize') return formatBytes(value);
        if (heading?.valueType === 'ms' || heading?.key.toLowerCase().includes('time')) return formatTime(value);
        return value.toLocaleString();
    }
    return value?.toString() || '—';
};

// --- Helper Functions ---

// Unify flattening logic: flatten both node and source fields
const flattenItem = (item: any) => {
    const flat: Record<string, any> = { ...item };
    if (item.node && typeof item.node === 'object') {
        Object.entries(item.node).forEach(([k, v]) => {
            flat[`node.${k}`] = v;
            if (["snippet", "selector", "label", "path"].includes(k)) {
                flat[k] = v;
            }
        });
    }
    if (item.source && typeof item.source === 'object') {
        Object.entries(item.source).forEach(([k, v]) => {
            flat[`source.${k}`] = v;
            if (["snippet", "selector", "label", "path"].includes(k)) {
                flat[k] = v;
            }
        });
    }
    return flat;
};

// Extract all unique keys from all items, prefer known fields first
const getRelevantKeys = (items: any[]): string[] => {
    if (!Array.isArray(items) || items.length === 0) return [];
    const preferred = [
        'snippet', 'selector', 'source', 'url', 'nodeLabel', 'node.snippet', 'node.selector', 'nodeLabel', 'node.path',
        'source.url', 'source.type', 'source.line', 'source.column', 'label', 'path', 'score', 'value', 'transferSize', 'wastedMs', 'wastedBytes', 'requestCount', 'size', 'type', 'priority', 'duration', 'startTime', 'endTime', 'event', 'passive', 'blocking', 'listener', 'element', 'attribute', 'property', 'message', 'description', 'summary', 'details', 'id', 'name', 'title', 'text', 'alt', 'src', 'href', 'tagName', 'className', 'role', 'roleDescription', 'node.devtoolsNodePath', 'node.explanation', 'node.origin', 'node.type', 'node.value', 'node.url', 'node.label', 'node.path', 'node.selector', 'node.snippet', 'node.score', 'node.transferSize', 'node.wastedMs', 'node.wastedBytes', 'node.requestCount', 'node.size', 'node.type', 'node.priority', 'node.duration', 'node.startTime', 'node.endTime', 'node.event', 'node.passive', 'node.blocking', 'node.listener', 'node.element', 'node.attribute', 'node.property', 'node.message', 'node.description', 'node.summary', 'node.details', 'node.id', 'node.name', 'node.title', 'node.text', 'node.alt', 'node.src', 'node.href', 'node.tagName', 'node.className', 'node.role', 'node.roleDescription',
    ];
    const allKeys = Array.from(new Set(items.flatMap(item => Object.keys(flattenItem(item)))));
    return preferred.concat(allKeys).filter((v, i, arr) => arr.indexOf(v) === i && allKeys.includes(v));
};

const renderDynamicTable = (items: any[]) => {
    const flatItems = items.map(flattenItem);
    const keys = getRelevantKeys(flatItems);
    if (keys.length === 0) return <span className="text-muted-foreground">No affected elements/resources found.</span>;
    return (
        <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {keys.map((k, i) => (
                            <th key={i} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {k.replace('node.', '').replace('source.', '').toUpperCase()}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {flatItems.map((item, idx) => (
                        <tr key={idx}>
                            {keys.map((k, i) => (
                                <td key={i} className="px-4 py-2 whitespace-normal text-sm text-gray-800 align-top">{renderValue(item[k])}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// --- LCP Details Renderer ---
const LcpElementDetailsComponent: React.FC<{ details: LcpElementDetails }> = ({ details }) => {
    if (!details) return null;

    const { node, phases, lcpTiming, metricSavings } = details;

    return (
        <div className="space-y-4">
            {lcpTiming && (
                <div className="text-sm font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded inline-block">
                    LCP Time: {formatTime(lcpTiming)}
                    {metricSavings?.LCP && ` (potential savings: ${formatTime(metricSavings.LCP)})`}
                </div>
            )}

            {node.snippet && (
                <div>
                    <h4 className="font-semibold text-md mb-2">LCP Element</h4>
                    <pre className="bg-gray-900 text-white p-3 rounded-md text-xs font-mono overflow-x-auto"><code>{node.snippet}</code></pre>
                    {node.selector && <p className="text-xs text-muted-foreground mt-1">CSS Selector: <code>{node.selector}</code></p>}
                </div>
            )}

            {phases && phases.length > 0 && (
                <div>
                    <h4 className="font-semibold text-md mb-2">LCP Phases</h4>
                    <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phase</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timing</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {phases.map((phase, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2 whitespace-normal text-sm text-gray-800">{phase.phase}</td>
                                        <td className="px-4 py-2 whitespace-normal text-sm text-gray-800 font-mono">{formatTime(phase.timing * 1000)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

// Defensive fallback for extractedItems
const DiagnosticDetails: React.FC<{ details?: DiagnosticDetailsData, diagId?: string, extractedItems?: any[], lcpDetails?: LcpElementDetails | null }> = ({ details, diagId, extractedItems, lcpDetails }) => {
    if (diagId === 'largest-contentful-paint-element' && lcpDetails) {
        return <LcpElementDetailsComponent details={lcpDetails} />;
    }

    // If we have extractedItems (from custom extraction), always render dynamic table for these IDs
    if (ALWAYS_DYNAMIC_TABLE_IDS.includes(diagId || '')) {
        if (extractedItems && extractedItems.length) {
            return renderDynamicTable(extractedItems);
        } else {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn('No actionable extractedItems for diagnostic', diagId, extractedItems);
                // Optionally, log the raw details for further debugging
                if (details) {
                    // eslint-disable-next-line no-console
                    console.log('Raw diagnostic details:', diagId, details);
                }
            }
            return <span className="text-muted-foreground">No actionable elements found for this diagnostic.</span>;
        }
    }

    if (!details) {
        return <p className="text-muted-foreground text-sm">No details available for this diagnostic.</p>;
    }

    const copyTable = (table: DetailsTable) => {
        const headings = table.headings.map(h => h.label).join('\t');
        const rows = table.items.map(item =>
            table.headings.map(h => {
                const value = item[h.key];
                if (typeof value === 'object' && value !== null) return JSON.stringify(value);
                if ((h.valueType === 'bytes' || h.key === 'transferSize') && typeof value === 'number') return formatBytes(value);
                if (h.valueType === 'ms' || h.key.toLowerCase().includes('time')) return formatTime(value);
                return value?.toString() || '—';
            }).join('\t')
        ).join('\n');
        navigator.clipboard.writeText(`${headings}\n${rows}`);
        toast.success('Table copied to clipboard as TSV!');
    };

    switch (details.type) {
        case 'table':
        case 'opportunity':
            if (!details.items || details.items.length === 0) {
                return <p className="text-muted-foreground text-sm">No affected resources found.</p>;
            }
            // If headings are missing or empty, or items have no matching keys, use dynamic table
            if (!details.headings || details.headings.length === 0) {
                return renderDynamicTable(details.items);
            }
            let currentGroup: string | null = null;
            return (
                <div className="overflow-x-auto p-1">
                    <div className="flex justify-end items-center mb-2">
                        <Button variant="ghost" size="sm" onClick={() => copyTable(details)}>
                            <CopyIcon className="h-4 w-4 mr-2" />
                            Copy
                        </Button>
                    </div>
                    <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {details.headings.map((h, hIndex) => (
                                        <th key={hIndex} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {h.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {details.items.map((item, itemIndex) => {
                                    // Group header row
                                    if (item._groupHeader && item._groupHeader !== currentGroup) {
                                        currentGroup = item._groupHeader;
                                        return (
                                            <tr key={`group-${itemIndex}`}> 
                                                <td colSpan={details.headings.length} className="bg-muted font-bold text-sm px-6 py-2 border-b border-gray-200">{item._groupHeader}</td>
                                            </tr>
                                        );
                                    }
                                    return (
                                        <tr key={itemIndex}>
                                            {details.headings.map((h, hIndex) => (
                                                <td key={hIndex} className="px-6 py-4 whitespace-normal text-sm text-gray-800 align-top">
                                                    {/* Tags/labels */}
                                                    {item._tags && hIndex === 0 && (
                                                        <span className="mr-2">{item._tags.map((tag: string) => renderTag(tag))}</span>
                                                    )}
                                                    {/* Value */}
                                                    {renderValue(item[h.key], h)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            );

        case 'list':
            return (
                <ul className="list-disc list-inside text-sm bg-muted p-3 rounded mb-2 space-y-1">
                    {details.items.map((item, i) => (
                        <li key={i}>{renderValue(item.richText || item)}</li>
                    ))}
                </ul>
            );

        case 'screenshot':
            return <img src={details.data} alt="Diagnostic Screenshot" className="max-w-md max-h-80 rounded shadow border mb-2" />;

        case 'filmstrip':
            return (
                <div className="flex gap-2 overflow-x-auto p-1">
                    {details.items.map((item, i) => (
                        <img key={i} src={item.data} alt={`Filmstrip frame ${i + 1}`} className="max-h-60 rounded shadow border" />
                    ))}
                </div>
            );

        case 'pluginTip':
            return (
                <div className="flex items-center p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
                    {details.icon && <img src={details.icon} alt="" className="w-6 h-6 mr-3" />}
                    <span className="text-sm text-yellow-800">{details.message}</span>
                </div>
            );

        default:
            return <p className="text-muted-foreground text-sm">This diagnostic does not have a visual representation.</p>;
    }
};


// --- Main Component ---
export const Diagnostics: React.FC<DiagnosticsProps> = ({ reportData, openItems, setOpenItems }) => {
    if (!reportData?.diagnostics?.length) {
        return null;
    }

    return (
        <Card id="diagnostics" className="mb-8 border-0 shadow-none">
            <CardHeader className="border-b-0">
                <CardTitle className="text-2xl font-semibold text-card-foreground">Diagnostics</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
                <Accordion type="multiple" className="w-full space-y-4" value={openItems} onValueChange={setOpenItems}>
                    {reportData.diagnostics.map((diag, diagIndex) => (
                        <AccordionItem key={diagIndex} value={`item-${diagIndex}`} className="border rounded-lg">
                            {/* Section Header: Title, Est Savings, Severity Icon */}
                            <AccordionTrigger className="px-6 py-4 text-lg font-semibold hover:no-underline flex items-center gap-3 text-left">
                                {diag.severity && (
                                    <span className={`w-5 h-5 rounded-full ${diag.severity === 'error' ? 'bg-red-500' : diag.severity === 'warning' ? 'bg-yellow-400' : 'bg-blue-400'}`}></span>
                                )}
                                <span className="flex-1">{diag.title}</span>
                                {diag.estSavings && (
                                    <span className="text-xs font-bold text-red-600 ml-2">{diag.estSavings}</span>
                                )}
                            </AccordionTrigger>
                            <AccordionContent className="px-6 pb-6 space-y-4">
                                {/* Description & Plugin Tips */}
                                <p className="text-muted-foreground">
                                    {stripMarkdownLinks(diag.description)}
                                </p>
                                {diag.pluginTip && (
                                    <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded mb-2 flex items-center gap-2">
                                        {diag.pluginTip.icon && <span className="text-blue-400">{diag.pluginTip.icon}</span>}
                                        <span className="text-blue-900 text-sm">{diag.pluginTip.message}</span>
                                    </div>
                                )}
                                {/* Score, Display Value, Numeric Value */}
                                <div className="flex flex-wrap gap-4 items-center">
                                    {typeof diag.score === 'number' && (
                                        <span className={`text-sm font-bold px-2 py-1 rounded ${getScoreColor(diag.score)} bg-opacity-10`}>
                                            Score: {diag.score}
                                        </span>
                                    )}
                                    {diag.displayValue && (
                                        <span className="text-sm bg-muted px-2 py-1 rounded">{diag.displayValue}</span>
                                    )}
                                    {typeof diag.numericValue === 'number' && (
                                        <span className="text-sm text-muted-foreground">
                                            Value: <span className="font-mono">{diag.numericValue.toLocaleString()}</span>
                                        </span>
                                    )}
                                </div>
                                {/* LCP Timing (special case) - This is now handled by LcpElementDetailsComponent */}
                                {/* Table/List/Other Details */}
                                <DiagnosticDetails details={diag.details} diagId={diag.id} extractedItems={diag.extractedItems} lcpDetails={diag.lcpDetails} />
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </CardContent>
        </Card>
    );
};

export default Diagnostics;

// --- Helper Functions ---
const getScoreColor = (score: number | null | undefined) => {
    if (score === null || score === undefined) return 'text-muted-foreground';
    if (score >= 0.9) return 'text-report-good';
    if (score >= 0.5) return 'text-report-improvement';
    return 'text-report-fail';
};

// Shared helper to remove markdown links and their surrounding sentences/phrases
export function stripMarkdownLinks(text?: string): string {
  if (!text) return '';
  // Remove entire sentences containing markdown links
  // 1. Remove sentences like 'Learn more about ... [text](url).' or '... [text](url).' (with or without period)
  let result = text.replace(/[^.!?]*\[[^\]]+\]\([^\)]+\)[^.?!]*[.!?]?/g, '');
  // 2. Remove any remaining markdown links (if embedded in a sentence)
  result = result.replace(/\[[^\]]+\]\([^\)]+\)/g, '');
  // 3. Clean up extra whitespace
  return result.replace(/\s{2,}/g, ' ').trim();
}

// --- Diagnostic Field Mapping Helper ---
// This mapping is generated from docs/diagnostics-field-mapping.md and should be kept in sync.
const DIAGNOSTIC_FIELD_MAP: Record<string, { path: string; fields: string[]; display: 'table' | 'list' | 'code' | 'badge' | 'default' }> = {
    'lcp-lazy-loaded': {
        path: 'lcp-lazy-loaded.details.items',
        fields: ['url', 'isLazyLoaded', 'node.snippet'],
        display: 'table',
    },
    'largest-contentful-paint-element': {
        path: 'largest-contentful-paint-element.details.node',
        fields: ['snippet', 'selector', 'boundingRect', 'devtoolsNodePath'],
        display: 'code',
    },
    'uses-passive-event-listeners': {
        path: 'uses-passive-event-listeners.details.items',
        fields: ['url', 'line', 'col', 'type', 'passive'],
        display: 'table',
    },
    // ...add more mappings as needed from diagnostics-field-mapping.md...
};

// --- Patch: Use correct R2 wrapper for audit extraction ---
// Instead of passing only audits, pass the full reportData and extract audits from the correct path.
// Helper to get audits from the wrapped R2 object
function getLighthouseAudits(reportData: any): any {
    // Support both direct and wrapped (pagespeedData.lighthouseResult.audits) structures
    if (reportData?.pagespeedData?.lighthouseResult?.audits) {
        return reportData.pagespeedData.lighthouseResult.audits;
    }
    if (reportData?.lighthouseResult?.audits) {
        return reportData.lighthouseResult.audits;
    }
    if (reportData?.audits) {
        return reportData.audits;
    }
    return {};
}

// --- Robust extraction for all items, not just the first ---
function extractLcpElement(reportData: any) {
    const audits = getLighthouseAudits(reportData);
    const details = audits['largest-contentful-paint-element']?.details;
    if (details?.items?.length) {
        // Flatten all node items
        return details.items.map((item: any) => item.node ? flattenItem(item.node) : flattenItem(item));
    }
    // Fallback: try lcp-lazy-loaded node(s)
    const lcpLazyItems = audits['lcp-lazy-loaded']?.details?.items;
    if (lcpLazyItems?.length) return lcpLazyItems.map((i: any) => flattenItem(i.node || i));
    // Fallback: try prioritize-lcp-image debugData
    const lcpUrl = audits['prioritize-lcp-image']?.details?.debugData?.initiatorPath?.[0]?.url;
    if (lcpUrl) return [{ url: lcpUrl }];
    return [];
}

function extractLcpLazyLoaded(reportData: any) {
    const audits = getLighthouseAudits(reportData);
    const items = audits['lcp-lazy-loaded']?.details?.items;
    if (items && items.length) {
        return items.map((i: any) => flattenItem(i.node || i));
    }
    return [];
}

function extractPassiveListeners(reportData: any) {
    const audits = getLighthouseAudits(reportData);
    const items = audits['uses-passive-event-listeners']?.details?.items;
    if (items && items.length) {
        return items.map((i: any) => flattenItem(i.source || i));
    }
    return [];
}

export function extractDiagnosticFields(reportData: any, id: string): any {
    if (id === 'largest-contentful-paint-element') return extractLcpElement(reportData);
    if (id === 'lcp-lazy-loaded') return extractLcpLazyLoaded(reportData);
    if (id === 'uses-passive-event-listeners') return extractPassiveListeners(reportData);

    const lhAudits = getLighthouseAudits(reportData);
    const map = DIAGNOSTIC_FIELD_MAP[id];
    if (!map) return null;
    const pathParts = map.path.split('.');
    let data = lhAudits[id];
    for (let i = 1; i < pathParts.length; i++) {
        if (!data) break;
        data = data[pathParts[i]];
    }
    if (!data) return null;
    if (map.display === 'table' && Array.isArray(data)) {
        return data.map((item: any) => {
            const flat = flattenItem(item);
            const filtered: Record<string, any> = {};
            map.fields.forEach(f => filtered[f] = flat[f] ?? flat[`node.${f}`] ?? '—');
            return filtered;
        });
    }
    if (map.display === 'code' && typeof data === 'object') {
        const flat = flattenItem(data);
        const filtered: Record<string, any> = {};
        map.fields.forEach(f => filtered[f] = flat[f] ?? flat[`node.${f}`] ?? '—');
        return filtered;
    }
    return data;
}

// --- Unified Details Renderer Component ---
const ALWAYS_DYNAMIC_TABLE_IDS = [
    'largest-contentful-paint-element',
    'lcp-lazy-loaded',
    'uses-passive-event-listeners',
];
